import { ActionMenu } from '../../../../utilities/action-menu/action-menu';
import { CollapsibleCardProps } from '../types';
import { Box, CardHeader, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

export type CollapsibleCardHeaderProps = Pick<
  CollapsibleCardProps,
  | 'title'
  | 'actions'
  | 'secondaryActions'
  | 'avatar'
  | 'badge'
  | 'chips'
  | 'collapsible'
  | 'endContent'
  | 'onToggle'
  | 'onToggleOpen'
  | 'titleTypographyVariant'
  | 'isCompact'
  | 'hideExpander'
  | 'shouldShowPointer'
> & {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  rightComponent?: JSX.Element;
};

export const CollapsibleCardHeader = ({
  title,
  actions,
  secondaryActions,
  avatar,
  badge,
  chips,
  titleTypographyVariant,
  isCompact = false,
  hideExpander = false,
  shouldShowPointer = false,
  collapsible,
  endContent = null,
  open,
  rightComponent,
  setOpen,
  onToggle,
  onToggleOpen,
}: CollapsibleCardHeaderProps) => {
  return (
    <CardHeader
      title={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: '24px',
            rowGap: '18px',
            flexWrap: 'wrap',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: isCompact ? '12px' : '24px',
              flexGrow: 1,
            }}
          >
            {avatar}
            <Typography variant={titleTypographyVariant}>{title}</Typography>
            {badge && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  alignSelf: 'center',
                }}
              >
                {badge}
              </Box>
            )}
            {rightComponent && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'end',
                  flexGrow: 1,
                  flexDirection: 'column',
                }}
              >
                {rightComponent}
              </Box>
            )}
          </Box>
          {chips && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                columnGap: '8px',
                rowGap: '0px',
                flexWrap: 'wrap',
              }}
            >
              {chips}
            </Box>
          )}
        </Box>
      }
      action={
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
          {actions ? (
            <ActionMenu
              actions={actions}
              secondaryActions={secondaryActions}
              horizontalAlign="right"
            />
          ) : null}
          {endContent && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                height: '100%',
                ...(hideExpander && { paddingTop: '12px' }),
              }}
            >
              {endContent}
            </Box>
          )}
          {collapsible && !hideExpander && (
            <IconButton
              onClick={() => {
                setOpen(!open);
                if (onToggle) {
                  onToggle();
                }
                if (onToggleOpen && !open) {
                  onToggleOpen();
                }
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </Box>
      }
      onClick={
        hideExpander
          ? () => {
              if (onToggle) {
                onToggle();
              }
              if (onToggleOpen && !open) {
                onToggleOpen();
              }
              setOpen(!open);
            }
          : null
      }
      sx={{
        ...(shouldShowPointer && { '&:hover': { cursor: 'pointer' } }),
      }}
    />
  );
};
